// Ergosource Industrial Theme - Global JavaScript

class ErgosourceTheme {
  constructor() {
    this.init();
  }

  init() {
    this.setupMobileMenu();
    this.setupMegaMenu();
    this.setupScrollBehavior();
    this.setupCartDrawer();
    this.setupQuoteForm();
    this.setupResourceFilters();
    this.setupSearchModal();
  }

  // Mobile Menu Toggle - Enhanced with nested navigation and swipe gestures
  setupMobileMenu() {
    const menuToggle = document.querySelector('[data-mobile-menu-toggle]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    const menuClose = document.querySelector('[data-mobile-menu-close]');
    const menuBack = document.querySelector('[data-mobile-menu-back]');
    const menuTitle = document.querySelector('[data-mobile-menu-title]');
    const panelsContainer = document.querySelector('[data-mobile-menu-panels]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');

    if (!menuToggle || !mobileMenu) return;

    // Navigation stack for tracking panel history
    let navigationStack = ['main'];
    let scrollPositions = {};

    // Open mobile menu
    const openMenu = () => {
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      menuToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('menu-open');
      
      // Store scroll position before fixing body
      scrollPositions.body = window.pageYOffset;
      document.body.style.top = `-${scrollPositions.body}px`;
      
      // Trap focus
      trapFocus(mobileMenu);
      
      // Focus close button
      setTimeout(() => menuClose?.focus(), 100);
    };

    // Close mobile menu
    const closeMenu = () => {
      mobileMenu.classList.remove('is-open');
      mobileMenu.setAttribute('aria-hidden', 'true');
      menuToggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
      
      // Restore scroll position
      document.body.style.top = '';
      window.scrollTo(0, scrollPositions.body || 0);
      
      // Reset navigation to main panel
      setTimeout(() => {
        resetNavigation();
      }, 350);
      
      menuToggle.focus();
    };

    // Navigate to a panel
    const navigateToPanel = (panelId) => {
      const currentPanelId = navigationStack[navigationStack.length - 1];
      const currentPanel = panelsContainer?.querySelector(`[data-mobile-menu-panel="${currentPanelId}"]`);
      const nextPanel = panelsContainer?.querySelector(`[data-mobile-menu-panel="${panelId}"]`);

      if (!currentPanel || !nextPanel) return;

      // Store scroll position of current panel
      scrollPositions[currentPanelId] = currentPanel.scrollTop;

      // Animate panels
      currentPanel.classList.remove('mobile-menu__panel--active');
      currentPanel.classList.add('mobile-menu__panel--prev');
      nextPanel.classList.add('mobile-menu__panel--active');

      // Update navigation stack
      navigationStack.push(panelId);

      // Update header
      updateMenuHeader();

      // Reset scroll and focus
      nextPanel.scrollTop = 0;
      const firstLink = nextPanel.querySelector('.mobile-menu__link');
      firstLink?.focus();
    };

    // Navigate back
    const navigateBack = () => {
      if (navigationStack.length <= 1) return;

      const currentPanelId = navigationStack.pop();
      const previousPanelId = navigationStack[navigationStack.length - 1];
      const currentPanel = panelsContainer?.querySelector(`[data-mobile-menu-panel="${currentPanelId}"]`);
      const previousPanel = panelsContainer?.querySelector(`[data-mobile-menu-panel="${previousPanelId}"]`);

      if (!currentPanel || !previousPanel) return;

      // Animate panels
      currentPanel.classList.remove('mobile-menu__panel--active');
      previousPanel.classList.remove('mobile-menu__panel--prev');
      previousPanel.classList.add('mobile-menu__panel--active');

      // Restore scroll position
      previousPanel.scrollTop = scrollPositions[previousPanelId] || 0;

      // Update header
      updateMenuHeader();
    };

    // Reset navigation to main panel
    const resetNavigation = () => {
      navigationStack = ['main'];
      scrollPositions = {};
      
      panelsContainer?.querySelectorAll('.mobile-menu__panel').forEach((panel) => {
        panel.classList.remove('mobile-menu__panel--active', 'mobile-menu__panel--prev');
        if (panel.dataset.mobileMenuPanel === 'main') {
          panel.classList.add('mobile-menu__panel--active');
        }
      });

      updateMenuHeader();
    };

    // Update menu header (title and back button)
    const updateMenuHeader = () => {
      const isMainPanel = navigationStack.length <= 1;
      const currentPanelId = navigationStack[navigationStack.length - 1];
      const currentPanel = panelsContainer?.querySelector(`[data-mobile-menu-panel="${currentPanelId}"]`);
      
      // Toggle back button
      if (menuBack) {
        menuBack.hidden = isMainPanel;
      }

      // Update title
      if (menuTitle) {
        if (isMainPanel) {
          menuTitle.textContent = 'Menu';
        } else {
          menuTitle.textContent = currentPanel?.dataset.parentTitle || 'Menu';
        }
      }
    };

    // Event Listeners
    menuToggle.addEventListener('click', () => {
      if (mobileMenu.classList.contains('is-open')) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    menuClose?.addEventListener('click', closeMenu);
    menuBack?.addEventListener('click', navigateBack);
    overlay?.addEventListener('click', closeMenu);

    // Forward navigation buttons
    panelsContainer?.addEventListener('click', (e) => {
      const forwardBtn = e.target.closest('[data-mobile-nav-forward]');
      if (forwardBtn) {
        const targetPanel = forwardBtn.dataset.mobileNavForward;
        navigateToPanel(targetPanel);
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!mobileMenu.classList.contains('is-open')) return;

      if (e.key === 'Escape') {
        if (navigationStack.length > 1) {
          navigateBack();
        } else {
          closeMenu();
        }
      }
    });

    // Swipe to close gesture
    let touchStartX = 0;
    let touchStartY = 0;
    let touchMoveX = 0;
    const swipeThreshold = 80;

    mobileMenu.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchMoveX = touchStartX;
    }, { passive: true });

    mobileMenu.addEventListener('touchmove', (e) => {
      touchMoveX = e.touches[0].clientX;
      const touchMoveY = e.touches[0].clientY;
      
      // Only track horizontal swipes
      const deltaX = touchMoveX - touchStartX;
      const deltaY = Math.abs(touchMoveY - touchStartY);
      
      if (deltaX > 20 && deltaY < 50) {
        // Visual feedback during swipe
        const translateX = Math.min(deltaX * 0.5, 100);
        mobileMenu.style.transform = `translateX(${translateX}px)`;
      }
    }, { passive: true });

    mobileMenu.addEventListener('touchend', () => {
      const swipeDistance = touchMoveX - touchStartX;
      
      // Reset transform
      mobileMenu.style.transform = '';
      
      if (swipeDistance > swipeThreshold) {
        if (navigationStack.length > 1) {
          navigateBack();
        } else {
          closeMenu();
        }
      }
    }, { passive: true });
  }

  // Mega Menu Hover States
  setupMegaMenu() {
    const megaMenuItems = document.querySelectorAll('[data-mega-menu-trigger]');
    let closeTimeout;

    megaMenuItems.forEach((item) => {
      const dropdown = item.querySelector('[data-mega-menu-dropdown]');
      if (!dropdown) return;

      item.addEventListener('mouseenter', () => {
        clearTimeout(closeTimeout);
        this.closeAllMegaMenus();
        dropdown.classList.add('is-open');
        item.classList.add('is-active');
      });

      item.addEventListener('mouseleave', () => {
        closeTimeout = setTimeout(() => {
          dropdown.classList.remove('is-open');
          item.classList.remove('is-active');
        }, 150);
      });

      // Keyboard accessibility
      const trigger = item.querySelector('[data-mega-menu-button]');
      if (trigger) {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const isOpen = dropdown.classList.toggle('is-open');
          item.classList.toggle('is-active', isOpen);
          trigger.setAttribute('aria-expanded', isOpen);
        });

        trigger.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            dropdown.classList.remove('is-open');
            item.classList.remove('is-active');
            trigger.setAttribute('aria-expanded', 'false');
          }
        });
      }
    });
  }

  closeAllMegaMenus() {
    document.querySelectorAll('[data-mega-menu-dropdown].is-open').forEach((menu) => {
      menu.classList.remove('is-open');
      menu.closest('[data-mega-menu-trigger]')?.classList.remove('is-active');
    });
  }

  // Scroll behavior for sticky header
  setupScrollBehavior() {
    const header = document.querySelector('.header');
    if (!header || header.dataset.sticky !== 'true') return;

    let lastScroll = 0;
    const scrollThreshold = 100;

    window.addEventListener('scroll', () => {
      const currentScroll = window.pageYOffset;

      if (currentScroll > scrollThreshold) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }

      if (currentScroll > lastScroll && currentScroll > 300) {
        header.classList.add('header--hidden');
      } else {
        header.classList.remove('header--hidden');
      }

      lastScroll = currentScroll;
    }, { passive: true });
  }

  // Cart Drawer
  setupCartDrawer() {
    const cartToggle = document.querySelector('[data-cart-toggle]');
    const cartDrawer = document.querySelector('[data-cart-drawer]');
    const cartClose = document.querySelector('[data-cart-drawer-close]');
    const cartOverlay = document.querySelector('[data-cart-overlay]');

    if (!cartToggle || !cartDrawer) return;

    const openCart = () => {
      cartDrawer.classList.add('is-open');
      document.body.classList.add('cart-open');
      cartDrawer.querySelector('[data-cart-drawer-close]')?.focus();
    };

    const closeCart = () => {
      cartDrawer.classList.remove('is-open');
      document.body.classList.remove('cart-open');
      cartToggle.focus();
    };

    cartToggle.addEventListener('click', (e) => {
      e.preventDefault();
      openCart();
    });

    cartClose?.addEventListener('click', closeCart);
    cartOverlay?.addEventListener('click', closeCart);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && cartDrawer.classList.contains('is-open')) {
        closeCart();
      }
    });
  }

  // Quote Form Multi-step
  setupQuoteForm() {
    const form = document.querySelector('[data-quote-form]');
    if (!form) return;

    const steps = form.querySelectorAll('[data-quote-step]');
    const nextButtons = form.querySelectorAll('[data-quote-next]');
    const prevButtons = form.querySelectorAll('[data-quote-prev]');
    const indicators = form.querySelectorAll('[data-step-indicator]');
    let currentStep = 0;

    const showStep = (stepIndex) => {
      steps.forEach((step, index) => {
        step.classList.toggle('is-active', index === stepIndex);
        step.setAttribute('aria-hidden', index !== stepIndex);
      });

      indicators.forEach((indicator, index) => {
        indicator.classList.toggle('is-active', index === stepIndex);
        indicator.classList.toggle('is-complete', index < stepIndex);
      });

      currentStep = stepIndex;
    };

    const validateStep = (stepIndex) => {
      const step = steps[stepIndex];
      const requiredFields = step.querySelectorAll('[required]');
      let isValid = true;

      requiredFields.forEach((field) => {
        if (!field.value.trim()) {
          isValid = false;
          field.classList.add('is-invalid');
        } else {
          field.classList.remove('is-invalid');
        }
      });

      return isValid;
    };

    nextButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        if (validateStep(currentStep) && currentStep < steps.length - 1) {
          showStep(currentStep + 1);
        }
      });
    });

    prevButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        if (currentStep > 0) {
          showStep(currentStep - 1);
        }
      });
    });

    // Initialize
    showStep(0);
  }

  // Resource Downloads Filter
  setupResourceFilters() {
    const filterButtons = document.querySelectorAll('[data-resource-filter]');
    const resourceItems = document.querySelectorAll('[data-resource-item]');

    if (!filterButtons.length) return;

    filterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const filter = button.dataset.resourceFilter;

        // Update active state
        filterButtons.forEach((btn) => btn.classList.remove('is-active'));
        button.classList.add('is-active');

        // Filter items
        resourceItems.forEach((item) => {
          if (filter === 'all' || item.dataset.resourceType === filter) {
            item.style.display = '';
            item.classList.add('fade-in');
          } else {
            item.style.display = 'none';
            item.classList.remove('fade-in');
          }
        });
      });
    });
  }

  // Search Modal with Predictive Search
  setupSearchModal() {
    const searchToggle = document.querySelector('[data-search-toggle]');
    const searchModal = document.querySelector('[data-search-modal]');
    const searchClose = document.querySelector('[data-search-modal-close]');
    const searchOverlay = document.querySelector('[data-search-modal-overlay]');
    const searchInput = document.querySelector('[data-search-input]');
    const searchForm = document.querySelector('[data-search-form]');
    const predictiveContainer = document.querySelector('[data-predictive-search]');
    const loadingIndicator = document.querySelector('[data-predictive-loading]');
    const productsSection = document.querySelector('[data-predictive-products]');
    const productsList = document.querySelector('[data-predictive-products-list]');
    const pagesSection = document.querySelector('[data-predictive-pages]');
    const pagesList = document.querySelector('[data-predictive-pages-list]');
    const noResults = document.querySelector('[data-predictive-no-results]');
    const viewAllLink = document.querySelector('[data-view-all-link]');

    if (!searchToggle || !searchModal) return;

    let abortController = null;
    let cachedResults = {};

    // Open search modal
    const openSearch = () => {
      searchModal.classList.add('is-open');
      searchModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('search-open');
      
      // Focus input after animation
      setTimeout(() => {
        searchInput?.focus();
      }, 100);

      // Trap focus within modal
      trapFocus(searchModal);
    };

    // Close search modal
    const closeSearch = () => {
      searchModal.classList.remove('is-open');
      searchModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('search-open');
      
      // Clear results
      clearResults();
      if (searchInput) searchInput.value = '';
      
      // Return focus to toggle
      searchToggle.focus();
    };

    // Clear predictive results
    const clearResults = () => {
      predictiveContainer?.setAttribute('hidden', '');
      loadingIndicator?.setAttribute('hidden', '');
      productsSection?.setAttribute('hidden', '');
      pagesSection?.setAttribute('hidden', '');
      noResults?.setAttribute('hidden', '');
      viewAllLink?.setAttribute('hidden', '');
      if (productsList) productsList.innerHTML = '';
      if (pagesList) pagesList.innerHTML = '';
    };

    // Show loading state
    const showLoading = () => {
      predictiveContainer?.removeAttribute('hidden');
      loadingIndicator?.removeAttribute('hidden');
      productsSection?.setAttribute('hidden', '');
      pagesSection?.setAttribute('hidden', '');
      noResults?.setAttribute('hidden', '');
      viewAllLink?.setAttribute('hidden', '');
    };

    // Fetch predictive search results
    const fetchPredictiveSearch = async (query) => {
      if (query.length < 2) {
        clearResults();
        return;
      }

      // Check cache first
      if (cachedResults[query]) {
        renderResults(cachedResults[query], query);
        return;
      }

      // Abort previous request
      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();

      showLoading();

      try {
        const response = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,article&resources[limit]=6`,
          { signal: abortController.signal }
        );

        if (!response.ok) throw new Error('Search failed');

        const data = await response.json();
        cachedResults[query] = data;
        renderResults(data, query);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Predictive search error:', error);
          clearResults();
        }
      }
    };

    // Render search results
    const renderResults = (data, query) => {
      loadingIndicator?.setAttribute('hidden', '');
      
      const resources = data.resources?.results || {};
      const products = resources.products || [];
      const pages = resources.pages || [];
      const articles = resources.articles || [];
      const allPages = [...pages, ...articles];

      const hasProducts = products.length > 0;
      const hasPages = allPages.length > 0;

      if (!hasProducts && !hasPages) {
        // No results
        noResults?.removeAttribute('hidden');
        const querySpan = noResults?.querySelector('[data-search-query]');
        if (querySpan) querySpan.textContent = query;
        viewAllLink?.setAttribute('hidden', '');
        return;
      }

      predictiveContainer?.removeAttribute('hidden');

      // Render products
      if (hasProducts && productsList) {
        productsSection?.removeAttribute('hidden');
        productsList.innerHTML = products.map(product => `
          <li class="predictive-search__item">
            <a href="${product.url}" class="predictive-search__link">
              ${product.image ? `
                <div class="predictive-search__image">
                  <img 
                    src="${product.image.replace(/(\.[^.]+)$/, '_100x100$1')}" 
                    alt="${product.title}"
                    width="50"
                    height="50"
                    loading="lazy"
                  >
                </div>
              ` : `
                <div class="predictive-search__image predictive-search__image--placeholder">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="24" height="24">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                </div>
              `}
              <div class="predictive-search__details">
                <span class="predictive-search__title">${product.title}</span>
                <span class="predictive-search__price">${product.price}</span>
              </div>
            </a>
          </li>
        `).join('');
      } else {
        productsSection?.setAttribute('hidden', '');
      }

      // Render pages and articles
      if (hasPages && pagesList) {
        pagesSection?.removeAttribute('hidden');
        pagesList.innerHTML = allPages.map(page => `
          <li class="predictive-search__item predictive-search__item--page">
            <a href="${page.url}" class="predictive-search__link">
              <span class="predictive-search__page-title">${page.title}</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </a>
          </li>
        `).join('');
      } else {
        pagesSection?.setAttribute('hidden', '');
      }

      // Show view all link
      viewAllLink?.removeAttribute('hidden');
      viewAllLink?.setAttribute('href', `/search?q=${encodeURIComponent(query)}`);
      const queryLinkSpan = viewAllLink?.querySelector('[data-search-query-link]');
      if (queryLinkSpan) queryLinkSpan.textContent = query;
    };

    // Debounced search
    const debouncedSearch = debounce((query) => {
      fetchPredictiveSearch(query);
    }, 300);

    // Event Listeners
    searchToggle.addEventListener('click', (e) => {
      e.preventDefault();
      openSearch();
    });

    searchClose?.addEventListener('click', closeSearch);
    searchOverlay?.addEventListener('click', closeSearch);

    // Input handler for predictive search
    searchInput?.addEventListener('input', (e) => {
      const query = e.target.value.trim();
      if (query.length >= 2) {
        debouncedSearch(query);
      } else {
        clearResults();
      }
    });

    // Form submission - update view all link and submit
    searchForm?.addEventListener('submit', (e) => {
      const query = searchInput?.value.trim();
      if (!query) {
        e.preventDefault();
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!searchModal.classList.contains('is-open')) return;

      if (e.key === 'Escape') {
        closeSearch();
      }
    });

    // Close on click outside content (but inside modal)
    searchModal.addEventListener('click', (e) => {
      if (e.target === searchModal) {
        closeSearch();
      }
    });
  }
}

// Utility functions
function debounce(fn, delay) {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), delay);
  };
}

function trapFocus(container) {
  const focusableElements = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  container.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;

    if (e.shiftKey && document.activeElement === firstFocusable) {
      e.preventDefault();
      lastFocusable.focus();
    } else if (!e.shiftKey && document.activeElement === lastFocusable) {
      e.preventDefault();
      firstFocusable.focus();
    }
  });
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.ergosource = new ErgosourceTheme();
});

// Expose utilities globally
window.ErgosourceUtils = {
  debounce,
  trapFocus
};

